/**
 * Cover URL 缓存
 *
 * 在前端按资源 key 缓存预签名 cover URL，避免每次列表筛选/排序都拿到新签名 URL
 * 而导致浏览器图片缓存失效的问题。
 *
 * 缓存项的过期时间由 URL 自带的签名参数解析得到，再减去一段安全缓冲：
 *   - 阿里云 OSS V4：`x-oss-date` + `x-oss-expires`（秒）
 *   - 阿里云 OSS V1：`Expires`（Unix 秒级时间戳）
 *   - AWS S3：`X-Amz-Date` + `X-Amz-Expires`（秒）
 *   - GCS：`X-Goog-Date` + `X-Goog-Expires`（秒）
 *
 * 解析失败的 URL 不会被缓存，调用方表现等同于改动前。
 */

/** 临近过期时提前失效的安全缓冲，避免缓存命中但实际 URL 已过期 */
const SAFETY_BUFFER_MS = 5 * 60 * 1000;

interface CacheEntry {
    url: string;
    /** Unix 毫秒时间戳 */
    expireAt: number;
}

const cache = new Map<string, CacheEntry>();

/**
 * 把紧凑形式的 ISO 时间字符串（例如 `20260512T063250Z`）解析成 Unix 毫秒时间戳。
 * 格式不识别时返回 `null`。
 */
function parseCompactIsoDate(value: string): number | null {
    const match = value.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    if (!match) return null;
    const [, y, m, d, hh, mm, ss] = match;
    const ts = Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    return Number.isFinite(ts) ? ts : null;
}

/**
 * 解析一条预签名下载 URL 的过期时间，返回 Unix 毫秒时间戳；
 * 当 URL 上没有可识别的签名参数时返回 `null`，调用方应放弃缓存这条 URL。
 */
export function parsePresignedExpireAt(url: string): number | null {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }
    const params = parsed.searchParams;

    // 阿里云 OSS V4 / AWS S3 SigV4 / GCS V4：<厂商>-date + <厂商>-expires（秒）
    const dateExpiresPairs: Array<[string, string]> = [
        ['x-oss-date', 'x-oss-expires'],
        ['X-Amz-Date', 'X-Amz-Expires'],
        ['X-Goog-Date', 'X-Goog-Expires'],
    ];
    for (const [dateKey, expiresKey] of dateExpiresPairs) {
        const dateRaw = params.get(dateKey);
        const expiresRaw = params.get(expiresKey);
        if (!dateRaw || !expiresRaw) continue;
        const startMs = parseCompactIsoDate(dateRaw);
        const expiresSec = Number(expiresRaw);
        if (startMs == null || !Number.isFinite(expiresSec)) continue;
        return startMs + expiresSec * 1000;
    }

    // 阿里云 OSS V1：Expires=<Unix 秒级时间戳>，是绝对过期时间
    const v1Expires = params.get('Expires');
    if (v1Expires) {
        const sec = Number(v1Expires);
        if (Number.isFinite(sec)) return sec * 1000;
    }

    return null;
}

/**
 * 根据资源 key 取出缓存中的 cover URL。命中且仍在安全窗口内时返回 URL，
 * 已过期或未命中时返回 `null`，并顺带清理掉过期项。
 */
export function getCachedCoverUrl(coverKey: string): string | null {
    const entry = cache.get(coverKey);
    if (!entry) return null;
    if (Date.now() >= entry.expireAt - SAFETY_BUFFER_MS) {
        cache.delete(coverKey);
        return null;
    }
    return entry.url;
}

/**
 * 把一条 cover URL 写入缓存。无法解析出过期时间的 URL 会被静默丢弃，
 * 这样下次请求仍会回源到后端，行为退化到改动前。
 */
export function setCachedCoverUrl(coverKey: string, url: string): void {
    const expireAt = parsePresignedExpireAt(url);
    if (expireAt == null) return;
    if (Date.now() >= expireAt - SAFETY_BUFFER_MS) return;
    cache.set(coverKey, { url, expireAt });
}

/**
 * 把一组资源 key 拆分成 `cached`（已经在缓存中可直接复用）
 * 和 `missing`（需要向后端请求）两部分。
 */
export function partitionCoverKeysByCache(coverKeys: string[]): {
    cached: Record<string, string>;
    missing: string[];
} {
    const cached: Record<string, string> = {};
    const missing: string[] = [];
    const seen = new Set<string>();
    for (const key of coverKeys) {
        if (seen.has(key)) continue;
        seen.add(key);
        const hit = getCachedCoverUrl(key);
        if (hit) {
            cached[key] = hit;
        } else {
            missing.push(key);
        }
    }
    return { cached, missing };
}

/** 仅供单测使用：清空缓存以隔离用例之间的状态。 */
export function __resetCoverUrlCacheForTests(): void {
    cache.clear();
}
